import { useState } from 'react';
import type { HeroModule, MediaAsset } from '@inboundhost/shared';
import { imagePreviewUrl } from '../../lib/images';
import { MediaPicker } from './MediaPicker';

interface Props {
  value: HeroModule | null;
  onSave: (v: HeroModule) => void;
  siteId: string;
}

export function HeroEditor({ value, onSave, siteId }: Props) {
  const [imageKey, setImageKey] = useState<string | undefined>(value?.imageKey);
  const [headline, setHeadline] = useState(value?.headline ?? '');
  const [subheadline, setSubheadline] = useState(value?.subheadline ?? '');
  const [ctaLabel, setCtaLabel] = useState(value?.ctaLabel ?? '');
  const [ctaHref, setCtaHref] = useState(value?.ctaHref ?? '');
  const [alt, setAlt] = useState(value?.alt ?? '');

  function save() {
    if (!imageKey) return;
    onSave({
      imageKey,
      headline: headline || undefined,
      subheadline: subheadline || undefined,
      ctaLabel: ctaLabel || undefined,
      ctaHref: ctaHref || undefined,
      alt: alt || undefined,
    });
  }

  return (
    <div className="bg-white rounded shadow p-6 space-y-5">
      <div>
        <span className="block text-sm font-medium text-gray-700 mb-2">Banner image</span>
        {imageKey ? (
          <div className="relative inline-block">
            <img
              src={imagePreviewUrl(imageKey, { width: 480, height: 200, fit: 'cover' })}
              alt={alt}
              className="rounded border max-h-48 object-cover"
            />
            <button
              type="button"
              className="absolute top-1 right-1 bg-white/90 text-red-600 text-xs px-2 py-0.5 rounded"
              onClick={() => setImageKey(undefined)}
            >
              Remove
            </button>
          </div>
        ) : (
          <MediaPicker siteId={siteId} onSelect={(asset: MediaAsset) => setImageKey(asset.key)} />
        )}
      </div>

      <label className="block">
        <span className="block text-sm font-medium text-gray-700 mb-1">Headline</span>
        <input
          className="w-full border rounded px-3 py-2"
          value={headline}
          onChange={(e) => setHeadline(e.target.value)}
        />
      </label>

      <label className="block">
        <span className="block text-sm font-medium text-gray-700 mb-1">Subheadline</span>
        <input
          className="w-full border rounded px-3 py-2"
          value={subheadline}
          onChange={(e) => setSubheadline(e.target.value)}
        />
      </label>

      <div className="grid grid-cols-2 gap-4">
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">Button label</span>
          <input
            className="w-full border rounded px-3 py-2"
            value={ctaLabel}
            onChange={(e) => setCtaLabel(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">Button link</span>
          <input
            className="w-full border rounded px-3 py-2"
            placeholder="/menu or https://…"
            value={ctaHref}
            onChange={(e) => setCtaHref(e.target.value)}
          />
        </label>
      </div>

      <label className="block">
        <span className="block text-sm font-medium text-gray-700 mb-1">Image alt text</span>
        <input className="w-full border rounded px-3 py-2" value={alt} onChange={(e) => setAlt(e.target.value)} />
      </label>

      <div>
        <button
          type="button"
          onClick={save}
          disabled={!imageKey}
          className="bg-gray-900 text-white px-4 py-1 rounded hover:bg-gray-800 disabled:opacity-50"
        >
          Save hero
        </button>
        {!imageKey && <span className="ml-3 text-sm text-gray-500">Select a banner image to save.</span>}
      </div>
    </div>
  );
}
